import { createNode, DataValue } from '@glueapp/graph-types';

export const Combine = createNode({
    id: 'Combine',
    name: 'Combine',
    description:
        'The friendly fold: combine the items of a list pairwise into a single value of the SAME type using a combiner function (Accumulator and Item are both the element type). The first element seeds the fold; an empty list returns null. If your accumulator needs a different type than the list items, or you need a seed value, use "Reduce" instead. "Sum", "Max of List" and "Join Text List" are prebuilt combines.',
    aiDescription: [
        'Unseeded pairwise fold over a list: combines all items into a single value of the SAME type with a (Accumulator, Item) -> Result combiner. Async — uses executeFunctionNode; steps run sequentially because each depends on the previous accumulator.',
        'Use this when the accumulator and element types are the SAME (e.g. combine a list of numbers into one number). Because the first element seeds the fold, operations like multiply, min or max just work — no identity element needed. When the accumulator type DIFFERS from the element type, or you need an explicit seed, use "Reduce" instead. "Sum", "Max of List" and "Join Text List" are prebuilt combines.',
        'Setup steps:',
        '1. Create a function with inputs "Accumulator" (type T) and "Item" (type T) and output "Result" (type T).',
        '   Inside the function, produce the combined value as "Result".',
        '2. Add this node: glue add "Combine".',
        '3. Connect a list to "List".',
        '4. Bind the function to the "Combiner" port (kind: function) by SETTING its value to a function reference (not a connection): set_value <thisNodeId> "Combiner" {"isFunctionReferenceValue":true,"functionId":"<functionId>"}.',
        'Semantics: the first list item seeds the fold and the remaining items are combined into it left-to-right. An empty list returns null. Never throws.',
        'Output: "Result" (optional T), the combined value — null if the list is empty.',
    ].join('\n'),
    category: 'List',
    color: '#778ed5',
    isAsync: true, // Function execution is async

    inPorts: {
        List: {
            kind: 'array',
            subType: {
                kind: 'generic',
                identifier: 'T',
            },
        },
        Combiner: {
            kind: 'function',
            inputTypes: {
                Accumulator: { kind: 'generic', identifier: 'T' },
                Item: { kind: 'generic', identifier: 'T' },
            },
            outputTypes: {
                Result: { kind: 'generic', identifier: 'T' },
            },
        },
    },

    outPorts: {
        Result: { kind: 'generic', identifier: 'T' },
    },

    optionalOutPorts: ['Result'],

    execute: async ({ inPorts, executeFunctionNode }) => {
        if (!executeFunctionNode) {
            throw new Error('Function execution not available');
        }

        const list = inPorts['List'] as DataValue[];
        const combinerValue = inPorts['Combiner'];

        if (
            !combinerValue ||
            typeof combinerValue !== 'object' ||
            !('isFunctionReferenceValue' in combinerValue) ||
            !combinerValue.isFunctionReferenceValue
        ) {
            throw new Error('Combiner must be a function reference');
        }

        const combiner = combinerValue as { functionId: string };
        const items = Array.isArray(list) ? list : [];

        // No seed port: the first element seeds the fold, so there is no value
        // to return for an empty list — Result is an optional out port.
        if (items.length === 0) {
            return { output: { Result: null } };
        }

        let accumulator: DataValue = items[0];

        // Sequential fold — each step depends on the previous accumulator.
        for (let index = 1; index < items.length; index++) {
            const result = await executeFunctionNode(combiner.functionId, {
                Accumulator: accumulator,
                Item: items[index],
            });
            accumulator = result.output['Result'];
        }

        return { output: { Result: accumulator } };
    },
});
